/**
 * Unified signup funnel — surface tagging + normalization.
 *
 * The web app fires signup events under several legacy names, split by product flow
 * (`onboarding_signup_completed` with flow=onboarding|tms_onboarding|..., plus the
 * extension's `signup_success`, plus native-OAuth callbacks). Rather than rename every
 * fire site (which would break existing Amplitude charts / GA key events), we NORMALIZE
 * at the `track()` chokepoint into a single funnel event carrying a `surface` dimension.
 *
 * Result: ONE `signup_started` / `signup_completed` you can filter by `surface` for
 * per-surface numbers, or leave unfiltered for the company total — in Amplitude, GA4,
 * and Meta alike. No duplicated reports, and Meta gets one healthy conversion to optimize.
 */

/** Unified funnel event names emitted alongside the raw event. */
export enum FunnelEvent {
    SIGNUP_STARTED = 'signup_started',
    SIGNUP_COMPLETED = 'signup_completed',
}

/** The product surface a funnel event belongs to. */
export type Surface = 'web' | 'tms' | 'mobile' | 'extension' | 'loadhub'

/**
 * Maps the per-flow `flow` property (already set on most web signup events) to a surface.
 * `web` is the default bucket for the desktop /onboarding flow and anything unmapped.
 */
const FLOW_TO_SURFACE: Record<string, Surface> = {
    onboarding: 'web',
    mobile_onboarding: 'mobile',
    loadhub_onboarding: 'loadhub',
    tms_onboarding: 'tms',
}

/** Which product the visitor signalled intent for. Reported on signup/login events. */
type ProductIntent = 'tms' | 'spot'

/**
 * The product a flow sells. The shared OAuth hooks serve every flow, so they must derive this
 * rather than hardcode it — `useGoogleAuth`/`useOutlookAuth` used to send `product_intent: 'spot'`
 * even on the TMS page.
 *
 * Implemented THROUGH `resolveSurface` rather than reading the map directly, so the flow→surface
 * mapping cannot be applied two different ways. Note this takes a flow, not a props object:
 * `resolveSurface` lets an explicit `surface` prop override the flow, and that override is
 * deliberately not reachable here — callers pass the flow they are on, and no signup site sends
 * both. Do not "fix" this by widening the parameter without deciding which of the two wins.
 */
export const productIntentForFlow = (flow?: string): ProductIntent => (resolveSurface({ flow }) === 'tms' ? 'tms' : 'spot')

/** Resolves a surface from a funnel event's properties (flow / source / explicit surface). */
export const resolveSurface = (props?: Record<string, unknown>): Surface => {
    const explicit = props?.surface
    if (explicit === 'web' || explicit === 'tms' || explicit === 'mobile' || explicit === 'extension' || explicit === 'loadhub') {
        return explicit
    }
    const flow = typeof props?.flow === 'string' ? props.flow : undefined
    if (flow && FLOW_TO_SURFACE[flow]) return FLOW_TO_SURFACE[flow]
    // The invite flow uses `source: 'invite'` and has no flow — treat as web.
    return 'web'
}

/** Normalizes the many `method` spellings into a stable enum-like value. */
export const normalizeMethod = (method?: unknown): 'email' | 'google' | 'outlook' | 'other' => {
    const m = typeof method === 'string' ? method.toLowerCase() : ''
    if (m.includes('google') || m.includes('gmail')) return 'google'
    if (m.includes('outlook') || m.includes('microsoft')) return 'outlook'
    if (m.includes('email') || m.includes('password')) return 'email'
    return 'other'
}

/**
 * The set of raw event names that mean "signup started" / "signup completed". Fired
 * across web + extension. Native-OAuth callbacks emit `signup_completed` explicitly
 * (with an explicit `surface`) so they route through the COMPLETED branch too.
 */
const RAW_STARTED = new Set<string>(['onboarding_signup_started', 'sign_up_started'])
const RAW_COMPLETED = new Set<string>(['onboarding_signup_completed', 'signup_success', 'signup_completed'])

/** Classifies a raw event name into a funnel step, or null if it isn't a funnel event. */
export const classifyFunnelEvent = (rawEventName: string): FunnelEvent | null => {
    if (RAW_COMPLETED.has(rawEventName)) return FunnelEvent.SIGNUP_COMPLETED
    if (RAW_STARTED.has(rawEventName)) return FunnelEvent.SIGNUP_STARTED
    return null
}
